
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Loader2 } from "lucide-react";

interface AssociationFieldsProps {
  form: any;
}

export const AssociationFields = ({ form }: AssociationFieldsProps) => {
  const { currentOrganization } = useOrganization();

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ["leads", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) return [];
      
      const { data, error } = await supabase
        .from("leads")
        .select("id, name")
        .eq("organization_id", currentOrganization.id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentOrganization?.id,
  });

  const { data: contacts = [], isLoading: contactsLoading } = useQuery({
    queryKey: ["contacts", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) return [];
      
      const { data, error } = await supabase
        .from("contacts")
        .select("id, name")
        .eq("organization_id", currentOrganization.id)
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentOrganization?.id,
  });

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ["users", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) return [];
      
      try {
        // Get organization members
        const { data: members, error: membersError } = await supabase
          .from("organization_members")
          .select("user_id")
          .eq("organization_id", currentOrganization.id);
        
        if (membersError) {
          console.error('Error fetching members:', membersError);
          throw membersError;
        }
        
        if (!members?.length) return [];
        
        // Get profiles for those users
        const userIds = members.map(m => m.user_id);
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", userIds);
        
        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          throw profilesError;
        }
        
        return profiles || [];
      } catch (error) {
        console.error('Error fetching users:', error);
        return [];
      }
    },
    enabled: !!currentOrganization?.id,
  });

  // Helper function to handle select value changes
  const handleSelectChange = (value: string, fieldName: string) => {
    // Convert special values back to undefined for form submission
    const formValue = ['no-lead', 'no-contact', 'unassigned'].includes(value) ? undefined : value;
    form.setValue(fieldName, formValue);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Associations</h3>

      <FormField
        control={form.control}
        name="lead_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Lead</FormLabel>
            <Select 
              onValueChange={(value) => handleSelectChange(value, 'lead_id')} 
              value={field.value || "no-lead"}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select a lead (optional)">
                    {leadsLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  </SelectValue>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="no-lead">No lead</SelectItem>
                {leads.map((lead) => (
                  <SelectItem key={lead.id} value={lead.id}>
                    {lead.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="contact_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Contact</FormLabel>
            <Select 
              onValueChange={(value) => handleSelectChange(value, 'contact_id')} 
              value={field.value || "no-contact"}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select a contact (optional)">
                    {contactsLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  </SelectValue>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="no-contact">No contact</SelectItem>
                {contacts.map((contact) => (
                  <SelectItem key={contact.id} value={contact.id}>
                    {contact.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="assigned_to"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Assigned To</FormLabel>
            <Select 
              onValueChange={(value) => handleSelectChange(value, 'assigned_to')} 
              value={field.value || "unassigned"}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select assignee (optional)">
                    {usersLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  </SelectValue>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.full_name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
